import { AssigneeBadge, Button, CheckIcon, Dialog, DialogButtons, Spinner } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { useReducer } from 'react'
import { useProcess, useSelect } from '~/store'
import { BasicUser, FolderListRow } from '@st/sdk'
import { selIsBulkUpdatingAssignees, stFolderListModule } from './st-folder-list-module'
import { formatAssigneeInitials } from './assignee-utils'

type TriState = 'none' | 'some' | 'all'

type State = {
  initialByUserId: Record<string, TriState>
  selectionByUserId: Record<string, TriState>
}

type Action = { type: 'toggleUser'; userId: string }

export function STBulkAssignFoldersDialog({
  folderIds,
  folders,
  organizationUsers,
  onClose
}: {
  folderIds: string[]
  folders: FolderListRow[]
  organizationUsers: BasicUser[]
  onClose: () => void
}) {
  const folderList = useProcess('stFolderList')
  const saving = useSelect('stFolderList', (s) => selIsBulkUpdatingAssignees(s, folderIds))

  const [state, dispatch] = useReducer(
    reducer,
    { folders, organizationUsers },
    ({ folders, organizationUsers }): State => {
      const initial = computeInitialSelectionByUserId(folders, organizationUsers)
      return { initialByUserId: initial, selectionByUserId: initial }
    }
  )

  const selectionState = selSelectionState(state)

  const disabled = saving

  return (
    <Dialog
      title="Assign binders"
      subtitle={`Update assignees for ${folderIds.length} ${inflect(folderIds.length, 'binder')}.`}
      buttons={
        <DialogButtons>
          <Button variant="subtle" disabled={disabled} onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={disabled}
            leadingIcon={saving ? <Spinner className="size-4" /> : undefined}
            onClick={() => {
              const { addAssigneeIds, removeAssigneeIds } = selBulkAssigneeOperation(
                state,
                organizationUsers
              )

              if (removeAssigneeIds.length == 0 && addAssigneeIds.length == 0) {
                onClose()
                return
              }

              folderList.send({
                type: 'bulkUpdateFolderAssignees',
                folderIds,
                addAssigneeIds,
                removeAssigneeIds
              })
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="max-h-80 overflow-auto rounded-lg border border-gray-200">
          {organizationUsers.map((user) => {
            const triState = selectionState[user.id] ?? 'none'
            return (
              <BulkAssigneeRow
                key={user.id}
                user={user}
                triState={triState}
                disabled={disabled}
                onClick={() => dispatch({ type: 'toggleUser', userId: user.id })}
              />
            )
          })}
        </div>
      </div>
    </Dialog>
  )
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'toggleUser': {
      const next = { ...state.selectionByUserId }
      const current: TriState = next[action.userId] ?? 'none'
      next[action.userId] = current == 'none' ? 'all' : 'none'
      return { ...state, selectionByUserId: next }
    }
  }
}

function selSelectionState(state: State): Record<string, TriState> {
  return state.selectionByUserId
}

function selBulkAssigneeOperation(
  state: State,
  organizationUsers: BasicUser[]
): { addAssigneeIds: string[]; removeAssigneeIds: string[] } {
  const addAssigneeIds: string[] = []
  const removeAssigneeIds: string[] = []

  for (const user of organizationUsers) {
    const initial = state.initialByUserId[user.id] ?? 'none'
    const current = state.selectionByUserId[user.id] ?? 'none'

    if (current == 'none' && initial != 'none') {
      removeAssigneeIds.push(user.id)
    } else if (current == 'all' && initial != 'all') {
      addAssigneeIds.push(user.id)
    }
  }

  return { addAssigneeIds, removeAssigneeIds }
}

function computeInitialSelectionByUserId(
  folders: FolderListRow[],
  organizationUsers: BasicUser[]
): Record<string, TriState> {
  const assignedCountsByUserId = new Map<string, number>()
  for (const folder of folders) {
    for (const userId of folder.assigneeIds ?? []) {
      assignedCountsByUserId.set(userId, (assignedCountsByUserId.get(userId) ?? 0) + 1)
    }
  }

  const selectedCount = folders.length
  const initial: Record<string, TriState> = {}
  for (const user of organizationUsers) {
    const count = assignedCountsByUserId.get(user.id) ?? 0
    initial[user.id] = count == 0 ? 'none' : count == selectedCount ? 'all' : 'some'
  }
  return initial
}

function BulkAssigneeRow({
  user,
  triState,
  disabled,
  onClick
}: {
  user: BasicUser
  triState: TriState
  disabled: boolean
  onClick: () => void
}) {
  const isSelected = triState != 'none'
  return (
    <button
      type="button"
      disabled={disabled}
      className={
        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 disabled:cursor-default disabled:opacity-70 disabled:hover:bg-transparent'
      }
      onClick={onClick}
    >
      <AssigneeBadge color={isSelected ? 'gray' : 'outline'}>
        {formatAssigneeInitials(user)}
      </AssigneeBadge>
      <div className="min-w-0 truncate font-medium text-gray-900">{user.name || user.email}</div>
      {(triState == 'all' || triState == 'some') && (
        <CheckIcon className="ml-auto size-4 text-blue-500" />
      )}
    </button>
  )
}
